import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Field as FinalField, Form } from 'react-final-form';
import { useRecoilState } from 'recoil';
import { css } from '@emotion/react';
import {
	FilledInput,
	FormControl,
	FormLabel,
	Grid,
	IconButton,
	Input,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

import Button from 'components/shared/Button/Button';
import FormControlLabel from 'components/shared/FormControlLabel/FormControlLabel';
import { useColors } from 'hooks/useColors';
import { authApi } from 'lib/api/authApi';
import { userApi } from 'lib/api/userApi';
import { auth } from 'lib/auth';
import { required } from 'lib/form';
import Checkbox from 'src/components/shared/Checkbox/Checkbox';
import { SignUpGeneral } from 'src/types/userSettings';
import { userStore } from 'stores/userStore';
import { mobileOnly } from 'styles/utils';
import { IColors } from 'types/colors'; // import { colors } from 'styles/theme';
import { linkPaddingCss } from '../styles';
import { ResetPasswordLink } from '../ResetPasswordLink';
import { i18n } from 'lib/i18n';
import { ErrorText } from '../ErrorText';

const dialogTitleCss = (colors: IColors) => css`
	text-align: center;
	width: 100%;
	color: ${colors.tx_text_primary};

	&.MuiTypography-h5 {
		font-weight: 500;
		font-size: 2.2rem;
	}
`;
const termCss = css`
	padding: 12px 0;
`;

const dialogNotesCss = (colors: IColors) => css`
	text-align: center;
	width: 100%;
	color: ${colors.tx_text_primary};
	font-weight: 500;
	font-size: 14px;
`;

const termsCheckBoxCss = (colors: IColors) => css`
	text-decoration: unset;
	width: 16px;
	height: 16px;

	&.MuiCheckbox-root {
		color: ${colors.tx_text_primary};
		margin-right: 8px;
		.MuiSvgIcon-root {
			width: 20px;
			height: 20px;
		}
	}
`;

const toolTipCss = css`
	&.MuiIconButton-root {
		padding: 0 6px;
		color: #7c9cb4;

		.MuiSvgIcon-root {
			width: 14px;
			height: 14px;
		}
	}
`;

const userFormTwoControlCss = css`
	.MuiFormLabel-root {
		padding: 8px 0;
	}

	&.MuiFormControl-root {
		padding: 5px 0;
	}
`;

const loginInputCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};

	&.MuiInput-root {
		padding: 5px 10px;
	}

	&.MuiInput-root {
		margin-top: 0;
	}

	.MuiFilledInput-input {
		height: 20px;
	}
`;

const loginInputFailedCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};

	&.MuiInput-root {
		padding: 10px;
		margin: 0;
	}

	.MuiFilledInput-input {
		height: 40px;
	}

	border: 2px ${colors.bg_error} solid;
`;

const linkToTerms = css`
	text-decoration: underline;
`;

const btnHeightCss = css`
	height: 50px;
`;

const userFormControlCss = css`
	.MuiFormLabel-root {
		padding: 8px 0;
	}

	&.MuiFormControl-root {
		padding: 10px 0;
	}
`;

const familyRegistrationNote = css`
	margin-top: 12px;
	margin-bottom: 12px;
`;

interface SignUpBaseFormProps {
	invitationToken?: string;
	setNextStep: Dispatch<SetStateAction<boolean>>;
	defaulteEmail?: string;
	setIsForgotPass: (v: boolean) => void;
}

const SignUpBaseForm: FC<SignUpBaseFormProps> = props => {
	const { setNextStep, defaulteEmail, setIsForgotPass, invitationToken } = props;
	const { colors } = useColors();
	const [error, setError] = useState('');
	const isMobile = useMediaQuery(mobileOnly);
	const [loginSettings, setLoginSettings] = useRecoilState(userStore.loginSettings);

	const labels = i18n.loginDialog;
	const EXISTING_USER_ERR = labels.userAlreadyExist;

	const validateEmail = (email: string) => {
		// eslint-disable-next-line no-useless-escape
		// eslint-disable-next-line unicorn/no-unsafe-regex
		const re = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Z\\a-z-]+\.)+[A-Za-z]{2,}))$/;

		return re.test(String(email).toLowerCase());
	};

	const onSubmit = async (values: SignUpGeneral) => {
		try {
			await userApi.postSignUp({ ...values, invitationToken });

			// here login
			await authApi.login(values.email, values.password);

			if (!loginSettings?.isLogin) {
				// set login settings
				setLoginSettings({
					isLogin: true,
					userToken: auth.getToken(),
					userId: auth.getUserId(),
					isCompleteLoginProcess: false,
				});
			}

			setNextStep(true);
		} catch {
			setError(EXISTING_USER_ERR);
		}
	};

	return (
		<Form
			data-aid="SignUpBaseForm"
			onSubmit={onSubmit}
			validate={(values: SignUpGeneral) => {
				const errors: Record<string, unknown> = {};

				if (!validateEmail(values.email)) {
					errors.email = i18n.validationErrors.invalidEmail;
				}
				if (values.password && values.password.length <= 5) {
					errors.password = i18n.validationErrors.minPassWord;
				}
				if (values.confirm !== values.password) {
					errors.confirm = i18n.validationErrors.passwordMatch;
				}
				if (values.terms !== true) {
					errors.terms = i18n.validationErrors.invalidTC;
				}

				return errors;
			}}
			render={({ handleSubmit }) => (
				<form data-aid="SignUpBaseForm-form" autoComplete="off" onSubmit={handleSubmit}>
					<Typography component="h1" variant="h5" css={dialogTitleCss(colors)} id="login-dialog-title">
						{invitationToken ? labels.signupWithInviteTitle : labels.signUpTitle}
					</Typography>
					<div css={familyRegistrationNote}>
						<Typography css={dialogNotesCss(colors)} style={{ fontSize: '14px' }}>
							{labels.familySignupNotes.signup}
						</Typography>
					</div>
					<Grid container spacing={isMobile ? 0 : 4}>
						{/* First Name */}
						<Grid item sm={12} md={6}>
							<FinalField name="firstName" validate={required} displayEmpty>
								{({ input, meta }) => (
									<FormControl fullWidth css={userFormTwoControlCss}>
										<FormLabel htmlFor="firstName">
											{i18n.user.profile.firstName}
											{'*'}
										</FormLabel>
										<FilledInput
											required
											disableUnderline
											placeholder={i18n.user.profile.placeholderFirstName}
											css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
											autoComplete="email"
											{...input}
										/>
										{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
									</FormControl>
								)}
							</FinalField>
						</Grid>
						<Grid item sm={12} md={6}>
							{/* Last Name */}
							<FinalField name="lastName" validate={required} displayEmpty>
								{({ input, meta }) => (
									<FormControl fullWidth css={userFormTwoControlCss}>
										<FormLabel htmlFor="lastName">
											{i18n.user.profile.lastName}
											{'*'}
										</FormLabel>
										<FilledInput
											required
											disableUnderline
											placeholder={i18n.user.profile.placeholderLastName}
											css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
											autoComplete="email"
											{...input}
										/>
										{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
									</FormControl>
								)}
							</FinalField>
						</Grid>
						{/* Email Address */}
						<Grid item sm={12}>
							<FinalField name="email" validate={required} displayEmpty defaultValue={defaulteEmail}>
								{({ input, meta }) => (
									<FormControl fullWidth css={userFormControlCss}>
										<FormLabel htmlFor="loginEmail">
											{i18n.user.profile.emailAddress}
											{'*'}
										</FormLabel>
										<FilledInput
											// required
											disableUnderline
											placeholder={i18n.user.profile.placeholderEmailAddress}
											css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
											autoComplete="email"
											{...input}
										/>
										{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
									</FormControl>
								)}
							</FinalField>
						</Grid>
						<Grid item sm={12} md={6}>
							{/* Password */}
							<FinalField name="password" validate={required} displayEmpty>
								{({ input, meta }) => (
									<FormControl fullWidth css={userFormTwoControlCss}>
										<FormLabel htmlFor="loginPassword">
											{labels.password}
											{'*'}
											<Tooltip title={labels.toolTipTextPassword} arrow placement="right" css={toolTipCss}>
												<IconButton aria-label="delete">
													<InfoIcon />
												</IconButton>
											</Tooltip>
										</FormLabel>
										<Input data-testid="SignUpBaseForm-889D61" 
											required
											type="password"
											disableUnderline
											placeholder={labels.password}
											css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
											autoComplete="password"
											{...input}
										/>
										{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
									</FormControl>
								)}
							</FinalField>
						</Grid>
						<Grid item sm={12} md={6}>
							{/* Confirm Password */}
							<FinalField name="confirm" validate={required} displayEmpty>
								{({ input, meta }) => (
									<FormControl fullWidth css={userFormTwoControlCss}>
										<FormLabel htmlFor="confirmPassword">
											{labels.confirmPassword}
											{'*'}
										</FormLabel>
										<Input data-testid="SignUpBaseForm-312177" 
											required
											type="password"
											disableUnderline
											placeholder={labels.passwordRepeat}
											css={meta.error && meta.touched ? loginInputFailedCss(colors) : loginInputCss(colors)}
											autoComplete="confirm"
											{...input}
										/>
										{meta.error && meta.touched && <ErrorText colors={colors} withMargin={false} error={meta.error} />}
									</FormControl>
								)}
							</FinalField>
						</Grid>
					</Grid>
					<Grid container direction="column" justify="flex-end" alignItems="flex-start">
						{/* Terms and condition */}
						<Grid item xs css={linkPaddingCss(colors)}>
							<FinalField name="terms" component="input" type="checkbox">
								{({ input, meta }) => (
									<div css={termCss}>
										<FormControl fullWidth>
											<FormControlLabel
												control={<Checkbox  data-testid="SignUpBaseForm-D505F8" css={termsCheckBoxCss(colors)} color="default" {...input} />}
												label={
													<>
														{labels.termsLabel}
														<a  data-testid="SignUpBaseForm-569C59" target="_blank" rel="noreferrer" href={`/terms`} css={linkToTerms}>
															{labels.termsAndConditions}
														</a>
													</>
												}
											/>
											{meta.error && meta.touched && setError(meta.error)}
										</FormControl>
									</div>
								)}
							</FinalField>
						</Grid>
					</Grid>

					<Button type="submit" data-aid="button-SignUpBaseForm-submit" fullWidth variant="contained" color="secondary" css={btnHeightCss}>
						{labels.createAccount}
					</Button>
					<ErrorText colors={colors} error={error} />
					{error === EXISTING_USER_ERR && (
						<ResetPasswordLink
							colors={colors}
							labels={labels.resetPasswordTitle}
							callback={() => setIsForgotPass(true)}
						/>
					)}
				</form>
			)}
		/>
	);
};

export default SignUpBaseForm;
